import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import Topbar from "../Topbar";
import ContactDetailPage from "../ContactDetailPage";
import useEscapeLayer from "../useEscape";
import PersonPanel from "./PersonPanel";
import { SORTS, companyCell, howOn, num, personName } from "./listsModel.mjs";
import {
  AddToListMenu, BuiltIn, Icon, isTyping, NewListModal, UndoToast, addToList, moveToNoContact, useConflictAsk, useLists,
} from "./parts";
import "./lists.css";

/*
 * ONE LIST (frames 2, 3, 3b, 4). The table is people: their current email and
 * role, "3 roles" when there is more than one record of them, how they got on,
 * and the other lists they are on. A row opens the person beside the list.
 *
 * Weeding is the point of the page, so it is fast: tick rows (or "Select all
 * N"), then N moves them to No Contact with no dialog and an Undo toast, A adds
 * them to a list, R takes them off this one. Esc clears.
 *
 * The No Contact list is drawn by the same page with fewer verbs: the only bulk
 * action there is to take people off it.
 */

const PAGE = 100;

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}


export default function ListPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [{ lists }, reloadLists] = useLists();
  const [list, setList] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ member: 0, removed: 0 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("member");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const [source, setSource] = useState("");
  const [other, setOther] = useState("");
  const [multi, setMulti] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [menu, setMenu] = useState(null); // "bulk-add" | `row:${personId}` | `rowadd:${personId}`
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [peek, setPeek] = useState(null); // { personId, contactId? }
  const [newFrom, setNewFrom] = useState(null); // person ids for "+ New list from these"
  const [adding, setAdding] = useState(false);
  const conflictAsk = useConflictAsk("");
  const search = useDebounced(q, 250);
  const sentinel = useRef(null);
  const clearToast = useCallback(() => setToast(null), []);

  const suppression = list?.kind === "suppression";
  const query = useMemo(() => {
    const p = new URLSearchParams({ state: tab, sort });
    if (search.trim()) p.set("q", search.trim());
    if (source) p.set("source", source);
    if (other) p.set("other", other);
    if (multi) p.set("multi", "1");
    return p;
  }, [tab, sort, search, source, other, multi]);

  const load = useCallback(
    async ({ append = false } = {}) => {
      setLoading(true);
      try {
        const p = new URLSearchParams(query);
        p.set("limit", String(PAGE));
        p.set("offset", String(append ? rows.length : 0));
        const data = await api(`/lists/${id}/members?${p}`);
        setList(data.list);
        setTotal(data.total);
        setCounts(data.counts || { member: 0, removed: 0 });
        setRows((prev) => (append ? [...prev, ...data.members] : data.members));
        setError(null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    // rows.length only matters for an append, which reads it at call time.
    [id, query]
  );
  useEffect(() => {
    load();
    setSelected(new Set());
  }, [load]);

  // Rows load as you scroll.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || rows.length >= total) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !loading) load({ append: true });
    });
    io.observe(el);
    return () => io.disconnect();
  }, [rows.length, total, loading, load]);

  const refresh = useCallback(async () => {
    await Promise.all([load(), reloadLists()]);
  }, [load, reloadLists]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setMenu(null);
    }
  };

  const ids = [...selected];
  const nameOf = (pid) => {
    const r = rows.find((x) => x.person_id === pid);
    return r ? personName(r) : null;
  };
  const who = (pids) => (pids.length === 1 && nameOf(pids[0]) ? nameOf(pids[0]) : `${num(pids.length)}`);

  const doNoContact = (pids) =>
    run(async () => {
      // The toast names the OTHER lists it took them off; this one is on screen.
      const { toast: t } = await moveToNoContact(pids, { exceptListId: list.id });
      setSelected(new Set());
      await refresh();
      setToast({ ...t, title: pids.length === 1 && nameOf(pids[0]) ? `${nameOf(pids[0])} moved to No Contact` : t.title, undo: t.undo && (async () => { await t.undo(); await refresh(); }) });
    });

  const doRemove = (pids) =>
    run(async () => {
      const res = await api(`/lists/${list.id}/members/remove`, { method: "POST", body: JSON.stringify({ person_ids: pids }) });
      setSelected(new Set());
      await refresh();
      setToast({
        icon: false,
        title: suppression ? `${who(pids)} taken off No Contact` : `${who(pids)} removed from ${list.name}`,
        sub: suppression && res.restored ? `Back on ${num(res.restored)} ${res.restored === 1 ? "list" : "lists"} they were on` : null,
        undo: suppression
          ? async () => { await api("/lists/no-contact/members", { method: "POST", body: JSON.stringify({ person_ids: res.previous.map((r) => r.person_id) }) }); await refresh(); }
          : async () => { await api(`/lists/${list.id}/members/restore`, { method: "POST", body: JSON.stringify({ previous: res.previous }) }); await refresh(); },
      });
    });

  const doAdd = (target, pids) =>
    run(async () => {
      const res = await addToList(target, pids, conflictAsk.askFor(target.name));
      if (!res) return;
      setSelected(new Set());
      await refresh();
      setToast({
        icon: false,
        title: `${num(res.added)} added to ${target.name}`,
        sub: res.already ? `${num(res.already)} already on it` : res.unsuppressed ? `${num(res.unsuppressed)} taken off No Contact` : null,
        undo: res.addedIds?.length && !res.unsuppressed
          ? async () => { await api(`/lists/${target.id}/members/unadd`, { method: "POST", body: JSON.stringify({ person_ids: res.addedIds }) }); await refresh(); }
          : null,
      });
    });

  const selectAll = () =>
    run(async () => {
      const p = new URLSearchParams(query);
      p.set("ids_only", "1");
      const data = await api(`/lists/${id}/members?${p}`);
      setSelected(new Set(data.ids));
    });

  // How many of the selection each list already holds — from the rows on the
  // page, so only stated when every selected person is loaded.
  const alreadyOn = useMemo(() => {
    if (!selected.size) return null;
    const loaded = rows.filter((r) => selected.has(r.person_id));
    if (loaded.length !== selected.size) return null;
    const out = {};
    for (const r of loaded) for (const l of r.other_lists || []) out[l.id] = (out[l.id] || 0) + 1;
    return out;
  }, [rows, selected]);

  // N / A / R with rows selected; Esc clears.
  useEscapeLayer(selected.size > 0 && !menu && !peek, () => setSelected(new Set()));
  useEffect(() => {
    if (!selected.size || !list) return undefined;
    const key = (e) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey || busy || menu) return;
      const k = e.key.toLowerCase();
      if (k === "n" && !suppression && tab === "member") { e.preventDefault(); doNoContact([...selected]); }
      else if (k === "a" && !suppression) { e.preventDefault(); setMenu("bulk-add"); }
      else if (k === "r" && tab === "member") { e.preventDefault(); doRemove([...selected]); }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  });

  const toggle = (pid) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });
  const allShown = rows.length > 0 && rows.every((r) => selected.has(r.person_id));
  const toggleShown = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allShown) rows.forEach((r) => n.delete(r.person_id));
      else rows.forEach((r) => n.add(r.person_id));
      return n;
    });

  const otherLists = lists.filter((l) => l.kind === "standard" && l.id !== list?.id);
  const tabCount = tab === "member" ? counts.member : counts.removed;

  const rowActions = (r) => (
    <>
      {!suppression && tab === "member" && (
        <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => doNoContact([r.person_id])}>
          <Icon.ban size={12} />No Contact
        </button>
      )}
      {!suppression && (
        <span style={{ position: "relative" }}>
          <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => setMenu(`rowadd:${r.person_id}`)}>Add to list…</button>
        </span>
      )}
      {tab === "member" ? (
        <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => doRemove([r.person_id])} aria-label={`Remove ${personName(r)} from this list`}>
          {suppression ? "Take off" : "Remove"}
        </button>
      ) : (
        <button className="crm-btn crm-btn--secondary lst-btn-sm" type="button" disabled={busy} onClick={() => doAdd(list, [r.person_id])}>Put back</button>
      )}
    </>
  );

  return (
    <>
      <div style={{ padding: "14px 24px 0" }}><Link className="lst-crumb" to="/lists">Lists /</Link></div>
      <Topbar
        title={list?.name || " "}
        sub={list?.system ? <BuiltIn /> : list?.description || null}
      >
        {!suppression && (
          <button className="crm-btn crm-btn--secondary lst-desk-only" type="button" disabled title="Not built yet">
            Export CSV <span className="lst-proposed">PROPOSED</span>
          </button>
        )}
        <button className="crm-btn crm-btn--primary" type="button" onClick={() => setAdding(true)}>
          <Icon.plus />{suppression ? "Block people" : "Add people"}
        </button>
      </Topbar>

      {list?.rule && (
        <div className="lst-rulebar">
          <span className="lst-pill lst-pill--k">Rule</span>
          <span>{(list.rule_summary || []).join(" · ")}</span>
        </div>
      )}
      {suppression && (
        <div className="lst-rulebar lst-sub">
          Outranks every list. Adding someone here takes them off all other lists; taking them off puts them back. Automations can't add them anywhere.
        </div>
      )}

      <div className="lst-tabs" role="tablist">
        <button className={`lst-tab${tab === "member" ? " is-on" : ""}`} type="button" role="tab" aria-selected={tab === "member"} onClick={() => setTab("member")}>
          {suppression ? "Blocked" : "On the list"} <span className="lst-tab__n crm-num">{num(counts.member)}</span>
        </button>
        {!suppression && (
          <button className={`lst-tab${tab === "removed" ? " is-on" : ""}`} type="button" role="tab" aria-selected={tab === "removed"} onClick={() => setTab("removed")}>
            Removed by hand <span className="lst-tab__n crm-num">{num(counts.removed)}</span>
          </button>
        )}
      </div>

      {selected.size > 0 ? (
        <div className="lst-bulk" role="toolbar" aria-label="Bulk actions">
          <span className="lst-bulk__count crm-num">{num(selected.size)} selected</span>
          {selected.size < tabCount && total === tabCount && (
            <button className="lst-bulk__link" type="button" onClick={selectAll} disabled={busy}>Select all {num(tabCount)}</button>
          )}
          {selected.size < total && total !== tabCount && (
            <button className="lst-bulk__link" type="button" onClick={selectAll} disabled={busy}>Select all {num(total)} matches</button>
          )}
          <span style={{ width: 8 }} />
          {!suppression && tab === "member" && (
            <button className="lst-bbtn lst-bbtn--y" type="button" disabled={busy} onClick={() => doNoContact(ids)}>
              <Icon.ban size={13} />Move to No Contact <span className="lst-kbd">N</span>
            </button>
          )}
          {!suppression && (
            <button className="lst-bbtn" type="button" disabled={busy} aria-expanded={menu === "bulk-add"} onClick={() => setMenu(menu === "bulk-add" ? null : "bulk-add")}>
              Add to list… <span className="lst-kbd">A</span>
            </button>
          )}
          {tab === "member" ? (
            <button className={`lst-bbtn${suppression ? " lst-bbtn--y" : ""}`} type="button" disabled={busy} onClick={() => doRemove(ids)}>
              {suppression ? "Take off No Contact" : "Remove from this list"} <span className="lst-kbd">R</span>
            </button>
          ) : (
            <button className="lst-bbtn lst-bbtn--y" type="button" disabled={busy} onClick={() => doAdd(list, ids)}>Put back on {list.name}</button>
          )}
          <span style={{ flexGrow: 1 }} />
          <button className="lst-bulk__link" type="button" style={{ color: "var(--crm-white)" }} onClick={() => setSelected(new Set())}>Clear · Esc</button>
          {menu === "bulk-add" && (
            <AddToListMenu
              lists={otherLists}
              alreadyOn={alreadyOn}
              count={selected.size}
              onPick={(l) => doAdd(l, ids)}
              onNew={() => { setMenu(null); setNewFrom(ids); }}
              onClose={() => setMenu(null)}
              style={{ top: 48, left: 300 }}
            />
          )}
        </div>
      ) : (
        <div className="lst-bar">
          <label className="lst-search">
            <Icon.search />
            <input type="search" aria-label="Search people" placeholder="Search name, any of their emails, company" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {!suppression && (
            <select className="lst-select" aria-label="How they got on" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="">Any way on</option>
              {list?.rule && <option value="rule">Rule</option>}
              <option value="manual">Added by you</option>
              <option value="automation">Automation</option>
            </select>
          )}
          <select className="lst-select" aria-label="Other lists" value={other} onChange={(e) => setOther(e.target.value)}>
            <option value="">On any other list or none</option>
            <option value="any">On another list</option>
            {otherLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            <option value="none">On no other list</option>
          </select>
          <select className="lst-select" aria-label="Roles" value={multi ? "multi" : ""} onChange={(e) => setMulti(e.target.value === "multi")}>
            <option value="">Any number of roles</option>
            <option value="multi">More than one role</option>
          </select>
          <span style={{ flexGrow: 1 }} />
          {!suppression && tab === "member" && <span className="lst-sub lst-desk-only">Select rows, then <span className="lst-kbd" style={{ color: "var(--crm-black)" }}>N</span> moves them to No Contact</span>}
        </div>
      )}

      {error && <p className="crm-login__err" style={{ margin: "8px 24px" }}>{error}</p>}

      <div className="lst-tablebox">
        <table className="lst-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}><input type="checkbox" checked={allShown} onChange={toggleShown} aria-label="Select all on this page" /></th>
              <th>{sort === "name" ? "Name ↓" : "Name"}</th>
              <th>Current email</th>
              <th>{sort === "company" ? "Current company · title ↓" : "Current company · title"}</th>
              <th>{tab === "removed" ? "Taken off" : "How they got on"}</th>
              <th>Other lists</th>
              <th style={{ width: 250 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const co = companyCell(r);
              const how = howOn(r);
              const on = selected.has(r.person_id);
              return (
                <tr
                  key={r.person_id}
                  className={`${on ? "is-sel" : ""}${peek?.personId === r.person_id ? " is-open" : ""}`}
                  onClick={() => setPeek({ personId: r.person_id })}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={on} onChange={() => toggle(r.person_id)} aria-label={`Select ${personName(r)}`} />
                  </td>
                  <td>
                    <span className="lst-nm">{personName(r)}</span> {r.role_count > 1 && <span className="lst-roles">{r.role_count} roles</span>}
                  </td>
                  <td>{r.email || <span className="lst-ti">no email</span>}</td>
                  <td>
                    {co.kind === "was" && <><span className="lst-ti">No company on the newest role · was at </span><span className="lst-co">{co.company}</span></>}
                    {co.kind === "current" && <><span className="lst-co">{co.company}</span>{co.title && <span className="lst-ti"> · {co.title}</span>}</>}
                    {co.kind === "none" && <span className="lst-ti">{co.title || "—"}</span>}
                  </td>
                  <td className="lst-how">
                    {tab === "removed" ? <><b>By you</b>{r.changed_at ? ` · ${howOn({ ...r, added_at: r.changed_at }).date}` : ""}</> : <><b>{how.word}</b>{how.date ? ` · ${how.date}` : ""}</>}
                  </td>
                  <td>
                    <span className="lst-chips">{(r.other_lists || []).map((l) => <span key={l.id} className="lst-pill">{l.name}</span>)}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="lst-rowact" style={{ position: "relative" }}>
                      <span className="lst-rowact__hover">{rowActions(r)}</span>
                      <button className="lst-more lst-rowact__more" type="button" aria-label={`More for ${personName(r)}`} onClick={() => setMenu(`row:${r.person_id}`)}>⋯</button>
                      {menu === `row:${r.person_id}` && (
                        <div className="lst-pop" style={{ top: 28, right: 0, width: 220 }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{rowActions(r)}</div>
                          <button className="lst-opt" type="button" onClick={() => setMenu(null)}>Close</button>
                        </div>
                      )}
                      {menu === `rowadd:${r.person_id}` && (
                        <AddToListMenu
                          lists={otherLists.filter((l) => !(r.other_lists || []).some((o) => o.id === l.id))}
                          count={1}
                          onPick={(l) => doAdd(l, [r.person_id])}
                          onClose={() => setMenu(null)}
                          style={{ top: 30, right: 0 }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="crm-empty">
                  {search || source || other || multi
                    ? "Nobody here matches."
                    : tab === "removed"
                      ? "Nobody removed by hand."
                      : suppression
                        ? "Nobody is on No Contact yet."
                        : "Nobody on this list yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div ref={sentinel} />
        <div className="lst-foot">
          <span className="lst-sub crm-num">
            {rows.length ? `1–${num(rows.length)} of ${num(total)}` : ""}{rows.length < total ? " · rows load as you scroll" : ""}
          </span>
          <span className="lst-sub">
            Sort:{" "}
            {SORTS.map((s, i) => (
              <React.Fragment key={s.key}>
                {i > 0 && " · "}
                <button type="button" className={`lst-sortlink${sort === s.key ? " is-on" : ""}`} onClick={() => setSort(s.key)}>{s.label}</button>
              </React.Fragment>
            ))}
          </span>
        </div>
      </div>

      {peek && (
        <aside className="crm-peek crm-leads__peek">
          {peek.contactId ? (
            <>
              <div style={{ padding: "12px 20px 0" }}>
                <button type="button" className="rec-back" onClick={() => setPeek({ personId: peek.personId })}>← {nameOf(peek.personId) || "Person"}</button>
              </div>
              <ContactDetailPage
                key={peek.contactId}
                contactId={peek.contactId}
                embedded
                hostLabel={list?.name || "Lists"}
                onClose={() => setPeek(null)}
                onChange={refresh}
              />
            </>
          ) : (
            <PersonPanel
              key={peek.personId}
              personId={peek.personId}
              allLists={lists}
              onClose={() => setPeek(null)}
              onChange={refresh}
              onToast={(t) => setToast({ ...t, undo: t.undo && (async () => { await t.undo(); await refresh(); }) })}
              onOpenLead={(contactId) => setPeek({ personId: peek.personId, contactId })}
            />
          )}
        </aside>
      )}

      {adding && list && (
        <AddPeopleModal
          list={list}
          onClose={() => setAdding(false)}
          onAdd={async (pids) => {
            setAdding(false);
            if (suppression) await doNoContact(pids);
            else await doAdd(list, pids);
          }}
        />
      )}
      {newFrom && (
        <NewListModal
          count={newFrom.length}
          onClose={() => setNewFrom(null)}
          onCreated={async (created) => {
            const pids = newFrom;
            setNewFrom(null);
            await doAdd(created, pids);
            navigate(`/lists/${created.slug || created.id}`);
          }}
        />
      )}
      {conflictAsk.el}
      <UndoToast toast={toast} onDone={clearToast} />
    </>
  );
}

/** "Add people": find anyone by name, any of their emails, or company. */
function AddPeopleModal({ list, onClose, onAdd }) {
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [picked, setPicked] = useState(new Map());
  const search = useDebounced(q, 200);
  useEscapeLayer(true, onClose);
  useEffect(() => {
    let live = true;
    if (search.trim().length < 2) {
      setFound([]);
      return undefined;
    }
    api(`/people/search?q=${encodeURIComponent(search.trim())}`).then((d) => live && setFound(d.people)).catch(() => {});
    return () => { live = false; };
  }, [search]);
  const toggle = (p) =>
    setPicked((m) => {
      const n = new Map(m);
      if (n.has(p.person_id)) n.delete(p.person_id);
      else n.set(p.person_id, p);
      return n;
    });
  return (
    <div className="crm-modal__backdrop" onClick={onClose}>
      <div className="crm-modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__head"><h2>{list.kind === "suppression" ? "Block people" : `Add people to ${list.name}`}</h2></div>
        <div className="crm-modal__body">
          <label className="lst-search" style={{ flex: "none", width: "100%" }}>
            <Icon.search />
            <input type="search" autoFocus aria-label="Find people" placeholder="Name, any of their emails, company" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div style={{ maxHeight: 340, overflowY: "auto", border: found.length ? "var(--crm-border-divider)" : "none", borderRadius: 6 }}>
            {found.map((p) => {
              const already = (p.lists || []).some((l) => l.id === list.id);
              const blocked = (p.lists || []).some((l) => l.kind === "suppression");
              return (
                <label key={p.person_id} className="lst-who" style={{ cursor: already ? "default" : "pointer", opacity: already ? 0.55 : 1 }}>
                  <input type="checkbox" disabled={already} checked={already || picked.has(p.person_id)} onChange={() => toggle(p)} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <b>{personName(p)}</b>
                    <span className="lst-note"> · {[p.company, p.email].filter(Boolean).join(" · ")}</span>
                  </span>
                  {already && <span className="lst-note">already on</span>}
                  {!already && blocked && list.kind !== "suppression" && <span className="lst-nc-tag" style={{ fontSize: 10 }}><Icon.ban size={10} />No Contact</span>}
                </label>
              );
            })}
            {search.trim().length >= 2 && !found.length && <div className="lst-note" style={{ padding: 8 }}>Nobody matches.</div>}
          </div>
          <div className="lst-dlg__acts">
            <button type="button" className="crm-btn crm-btn--secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="crm-btn crm-btn--primary" disabled={!picked.size} onClick={() => onAdd([...picked.keys()])}>
              {list.kind === "suppression" ? `Block ${num(picked.size)}` : `Add ${num(picked.size)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
